import { z } from 'zod';
import { lenientDateString, lenientNumber } from '../schema-extensions';

// ============================================================================
// CONSTANTS (must be defined before schemas that reference them)
// ============================================================================
const RATE_CONSTANTS = {
  DATE_FORMAT_LENGTH: 10, // 'YYYY-MM-DD'
  MIN_RATE: 0,
  MAX_RATE: 100, // 100% rate maximum
  MIN_DURATION: 1, // At least 1 month
};

// ============================================================================
// SCHEMAS
// ============================================================================

/**
 * Lenient schema for rate - accepts various formats and coerces to standard
 */
const lenientRateNodeSchema = z.object({
  date: lenientDateString(''),
  rate: lenientNumber(0, RATE_CONSTANTS.MIN_RATE),
  rateDuration: z.union([
    lenientNumber(RATE_CONSTANTS.MIN_DURATION, RATE_CONSTANTS.MIN_DURATION),
    z.undefined()
  ]).optional(),
}).transform(val => ({
  date: val.date,
  rate: val.rate,
  rateDuration: val.rateDuration
}));

/**
 * Strict schema for rate - enforces exact format
 */
const strictRateNodeSchema = z.object({
  date: z.string().length(RATE_CONSTANTS.DATE_FORMAT_LENGTH),
  rate: z.number().min(RATE_CONSTANTS.MIN_RATE).max(RATE_CONSTANTS.MAX_RATE),
  rateDuration: z.number().min(RATE_CONSTANTS.MIN_DURATION).optional(),
}).transform(val => ({
  date: val.date,
  rate: val.rate,
  rateDuration: val.rateDuration
}));

// ============================================================================
// INTERFACE
// ============================================================================

export interface RateNode {
  date: string;
  rate: number;
  rateDuration?: number;
}

// ============================================================================
// NAMESPACE WITH METHODS
// ============================================================================

export namespace RateNode {
  /**
   * Safely deserialize unknown data into RateNode
   * Uses lenient parsing to handle various input formats
   */
  export function deserialize(data: unknown): RateNode {
    return lenientRateNodeSchema.parse(data);
  }

  /**
   * Create a new RateNode with validation
   * Uses strict parsing to ensure data integrity
   */
  export function create(data: RateNode): RateNode {
    return strictRateNodeSchema.parse(data);
  }

  /**
   * Validate that data conforms to RateNode structure
   */
  export function validate(data: unknown): data is RateNode {
    return strictRateNodeSchema.safeParse(data).success;
  }

  /**
   * Constants for validation and UI
   */
  export const Constants = RATE_CONSTANTS;
}
